<template>
  <div>
    <TanninChart v-show="mode === 'regional' || userMode === 'ripening'" class="chart"
                 :samples="filteredByYear"
                 :levels="levels"
                 :view_type="options.view.value"
    />
    <GraphSelector v-model="options"
                   :years="yearOptions"
                   :varieties="varietyOptions"
                   :locations="siteOptions"
    />

    <div class="site-descriptions" v-if="mode === 'regional'">
      <button class="red-button" @click="toggleSites()" style="margin: 15px">View Site Descriptions &raquo;</button>
      <table v-show="showSites">
        <tbody>
        <tr>
          <th>Site</th>
          <th>Location</th>
          <th>Tonnage</th>
          <th>Soil Type</th>
        </tr>
        <tr>
          <th>1</th>
          <td>East of Welland Canal</td>
          <td>Equal to or greater than 4 T/acre</td>
          <td>Sandy and loamy</td>
        </tr>
        <tr>
          <th>2</th>
          <td>East of Welland Canal</td>
          <td>Less than 4 T/acre</td>
          <td>Loamy sand</td>
        </tr>
        <tr>
          <th>3</th>
          <td>West of Welland Canal</td>
          <td>Equal to or greater than 4 T/acre</td>
          <td>Clay loam</td>
        </tr>
        <tr>
          <th>4</th>
          <td>West of Welland Canal</td>
          <td>Less than 4 T/acre</td>
          <td>Silty clay</td>
        </tr>
        </tbody>
      </table>
      <div v-show="showSites" style="margin-top: 15px">
        <strong>General features of sampling locations</strong>
        <br/><br/>
        <strong>Site 1</strong> is located east of the Welland canal on sandy and loamy sediments over clay loam till.
        The vines are trained on a four arm vertical divided canopy system that support a higher yield/vine.
        <br/><br/>
        <strong>Site 2</strong> is located east of the Welland canal on loamy sand. Chardonnay,
        Riesling and Pinot noir are trained on a two arm vertical shoot positioned system that support a lower
        yield/vine whereas Cabernet Franc and Cabernet Sauvignon are trained on a four arm vertical divided canopy
        system that support an average yield/vine.
        <br/><br/>
        <strong>Site 3</strong> is located west of the Welland canal on washed clay loam till. The vines are grown on a
        two arm vertical shoot positioned system that support an average yield/vine. Cabernet Sauvignon vines are grown
        on the same training system but support a lower yield/vine.
        <br/><br/>
        <strong>Site 4</strong> is located west of the Welland canal on heavier silty clay over clay loam soils.
        The vines are grown on a two arm vertical shoot positioned system. The vineyard produces lower yield/vine.
      </div>
    </div>

    <TanninLevelSummary :levels="levels" :options="options" />
    <TableComponent :data="filteredByYear"
                    sort-by="sample_date"
                    sort-order="desc"
                    :show-filter="false"
                    :show-caption="false">
      <TableColumn show="link" label="View">
        <template scope="row">
          <router-link :to="'/samples/' + row.id">View</router-link>
        </template>
      </TableColumn>
      <TableColumn show="sample_date" label="Sample Date">
        <template scope="row">
          <router-link :to="'/samples/' + row.id">{{row.sample_date}}</router-link>
        </template>
      </TableColumn>
      <TableColumn show="skin_tannin_coloured" label="Skin Tannin µg/berry">
        <template scope="row">
          <Popper trigger="hover" :options="{placement: 'top'}">
            <RangePopup
                        :range="levels[row.var_id].skin"
                        title="Skin Tannin"
                        :val="row.skin_tannin"></RangePopup>
            <span slot="reference">
              <span :class="getClass(row.skin_tannin, levels[row.var_id].skin)">
                {{row.skin_tannin}}
              </span>
            </span>
          </Popper>
        </template>
      </TableColumn>
      <TableColumn show="seed_tannin" label="Seed Tannin µg/berry">
        <template scope="row">
          <Popper trigger="hover" :options="{placement: 'top'}">
            <RangePopup
                        :range="levels[row.var_id].seed"
                        title="Seed Tannin"
                        :val="row.seed_tannin"></RangePopup>
            <span slot="reference">
              <span :class="getClass(row.seed_tannin, levels[row.var_id].seed)">
                {{row.seed_tannin}}
              </span>
            </span>
          </Popper>
        </template>
      </TableColumn>
      <TableColumn show="seeds_per_berry" label="Seeds/Berry"></TableColumn>
      <TableColumn show="berry_weight" label="Fresh Berry Weight (g)"></TableColumn>
      <TableColumn show="brix" label="Brix"></TableColumn>
      <TableColumn show="tit" label="TA g/L"></TableColumn>
      <TableColumn show="ph" label="pH"></TableColumn>
      <TableColumn show="ace" label="Acetic g/L"></TableColumn>
    </TableComponent>
  </div>
</template>

<script>
import Popper from 'vue-popperjs';
import { TableComponent, TableColumn } from 'vue-table-component';
import TanninChart from './TanninChart';
import GraphSelector from './SampleGraphSelector';
import TanninLevelSummary from './TanninLevelSummary';
import RangePopup from './RangePopup';

export default {
  name: 'TanninDisplayPage',
  components: {
    TanninChart,
    TableComponent,
    TableColumn,
    GraphSelector,
    RangePopup,
    Popper,
    TanninLevelSummary,
  },
  props: ['mode', 'userMode'],
  data() {
    return {
      showSites: false,
      options: {
        location: { label: 'Site 1', value: 24 },
        variety: { label: 'Cab Franc', value: 2 },
        year: { label: '2017', value: 2017 },
        view: { label: 'Skin & Seed Tannin', value: 'skin-seed' },
      },
    };
  },
  watch: {
    siteOptions() {
      if (this.siteOptions.length > 0) {
        this.options.location = this.siteOptions[0];
      }
    },
    varietyOptions() {
      if (this.varietyOptions.length > 0) {
        this.options.variety = this.varietyOptions[0];
      }
    },
    yearOptions() {
      if (this.yearOptions.length > 0) {
        this.options.year = this.yearOptions[0];
      }
    },
  },
  computed: {
    levels() {
      return this.$store.state.levels;
    },
    dataset() {
      if (this.mode === 'user') {
        return this.$store.state.samplesByUser;
      }
      return this.$store.state.publicSamples;
    },
    siteOptions() {
      const result = [...new Set(this.dataset.map(item => item.loc_id))];

      const results = [];

      this.$store.state.locationsAll.forEach((loc) => {
        if (result.includes(loc.id)) {
          results.push({ label: loc.name, value: loc.id });
        }
      });

      return results;
    },
    filteredBySite() {
      return this.dataset.filter(item => item.loc_id === this.options.location.value);
    },
    varietyOptions() {
      const result = [...new Set(this.filteredBySite.map(item => item.var_id))];

      const results = [];

      this.$store.state.varieties.forEach((variety) => {
        if (result.includes(variety.id)) {
          results.push({ label: variety.name, value: variety.id });
        }
      });

      return results;
    },
    filteredByVariety() {
      return this.filteredBySite.filter(item => item.var_id === this.options.variety.value);
    },
    yearOptions() {
      const allYears = [];
      this.filteredByVariety.forEach((item) => {
        allYears.push(parseInt(item.sample_date.split('-')[0], 10));
      });

      const result = [...new Set(allYears)];

      const finalResult = [];
      result.forEach((year) => {
        finalResult.push({ label: year, value: year });
      });
      return finalResult;
    },
    filteredByYear() {
      return this.filteredByVariety.filter(item => parseInt(item.sample_date.split('-')[0], 10) === this.options.year.value);
    },
  },
  methods: {
    toggleSites() {
      if (this.showSites) this.showSites = false;
      else this.showSites = true;
    },
    getClass(val, ranges) {
      let finalLevel = 0;

      if (val < ranges[1]) {
        finalLevel = 0;
      } else if (val >= ranges[1] && val <= ranges[2]) {
        finalLevel = 1;
      } else {
        finalLevel = 2;
      }
      return this.indexToLevelClass(finalLevel);
    },
    indexToLevelClass(index) {
      switch (index) {
        case 0:
          return 'tannin-cell low';
        case 1:
          return 'tannin-cell med';
        case 2:
          return 'tannin-cell high';
        default:
          return '';
      }
    },
  },
};
</script>

<style lang="scss" scoped>
  @import '../assets/_colors.scss';
  @import '../assets/_globals.scss';
  @import '../assets/_common.scss';

  .popover[x-placement='top']{
    margin-bottom: 15px;
    .popper__arrow{
      width: 0;
      height: 0;
      border-left: 10px solid transparent;
      border-right: 10px solid transparent;
      border-top: 10px solid #f1f1f1;
      position: absolute;
      bottom: -10px;
      left: calc(15rem / 2 - 5px);
      &:after {
        content: '';
        position: absolute;
        border-left: 11px solid transparent;
        border-right: 11px solid transparent;
        border-top: 11px solid #dddddd;
        top: -10px;
        left: -11px;
        z-index: -1;
      }
    }
  }
  .popover[x-placement^='bottom']{
    margin-top: 15px;
    .popper__arrow{
      width: 0;
      height: 0;
      border-left: 10px solid transparent;
      border-right: 10px solid transparent;
      border-bottom: 10px solid #f1f1f1;
      position: absolute;
      top: -10px;
      left: calc(15rem / 2 - 5px);
      &:after {
        content: '';
        position: absolute;
        border-left: 11px solid transparent;
        border-right: 11px solid transparent;
        border-bottom: 11px solid #dddddd;
        top: -1px;
        left: -11px;
        z-index: -1;
      }
    }
  }

  .chart{
    height: 400px;
    position: relative;
  }
  .my-samples{
    position: relative;
  }
</style>

<style lang="css">
.table-component {
    display: flex;
    flex-direction: column;
  }

  .table-component > *,
  .table-component > *:after,
  .table-component > *:before {
    position: relative;
  }

  .table-component__filter {
    align-self: flex-end;
  }

  .table-component__filter__field {
    padding: 0 1.25em 0 .75em;
    height: 2.5em;
    border: solid 2px #e0e0e0;
    border-radius: 2em;
    font-size: inherit;
  }

  .table-component__filter__clear {
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 2em;
    color: #007593;
    font-weight: bold;
    cursor: pointer;
  }

  .table-component__filter__field:focus {
    outline: 0;
    border-color: #007593;
  }

  .table-component__table-wrapper {
    overflow-x: auto;
    margin: 0.5rem 0 1rem 0;
    width: 100%;
    border: solid 1px #ddd;
    border-bottom: none;
  }

  .table-component__table {
    min-width: 100%;
    border-collapse: collapse;
    border-bottom: solid 1px #ddd;
    table-layout: fixed;
  }

  .table-component__table__caption {
    position: absolute;
    top: auto;
    left: -10000px;
    overflow: hidden;
    width: 1px;
    height: 1px;
  }

  .table-component__table th,
  .table-component__table td {
    padding: .75em 1.25em;
    vertical-align: center;
    text-align: left;
  }

  .table-component__table th {
    background-color: #e0e0e0;
    color: #555;
    /*text-transform: uppercase;*/
    white-space: nowrap;
    font-size: .85em;
  }

  .table-component__table tbody tr:nth-child(even) {
    background-color: #f0f0f0;
  }

  .table-component__table a {
    color: #007593;
  }

  .table-component__message {
    color: #999;
    font-style: italic;
  }

  .table-component__th--sort,
  .table-component__th--sort-asc,
  .table-component__th--sort-desc {
    text-decoration: underline;
    cursor: pointer;
    -webkit-user-select: none;
    -moz-user-select: none;
    -ms-user-select: none;
    user-select: none;
  }

  .table-component__th--sort-asc:after,
  .table-component__th--sort-desc:after {
    position: absolute;
    left: .25em;
    display: inline-block;
    color: #bbb;
  }

  .table-component__th--sort-asc:after {
    content: '↑';
  }

  .table-component__th--sort-desc:after {
    content: '↓';
  }
</style>



// WEBPACK FOOTER //
// src/components/TanninDisplayPage.vue